import { AlertTriangle, Phone } from "lucide-react";
import { useLang } from "../../i18n/LangContext";
import { VEHICLE_TYPES, COMMON_MAKES, COLORS } from "../../data/services";
import { Field, inputProps, ChoiceButton } from "./Field";
import { PhotoUpload } from "./PhotoUpload";

const YEARS = Array.from({ length: new Date().getFullYear() + 2 - 1970 }, (_, i) => new Date().getFullYear() + 1 - i);

export const StepVehicle = ({ draft, update, errors }) => {
  const { t } = useLang();
  const v = draft.vehicle;
  const set = (patch) => update("vehicle", patch);
  return (
    <fieldset className="space-y-6">
      <legend className="text-xl sm:text-2xl font-extrabold uppercase">{t("wizard.vehicleTitle")}</legend>

      <div>
        <p className="mb-2 text-sm font-semibold text-gray-200">{t("wizard.safeQ")} <span className="text-[#DC2626]">*</span></p>
        <div role="radiogroup" className="grid grid-cols-2 gap-3 max-w-sm">
          <ChoiceButton selected={v.safe_location === true} onClick={() => set({ safe_location: true })} testId="safe-location-yes">{t("wizard.yes")}</ChoiceButton>
          <ChoiceButton selected={v.safe_location === false} onClick={() => set({ safe_location: false })} testId="safe-location-no">{t("wizard.no")}</ChoiceButton>
        </div>
        {errors["vehicle.safe_location"] && <p role="alert" className="mt-1.5 text-sm text-red-400" data-testid="vehicle-safe-error">{t(errors["vehicle.safe_location"])}</p>}
      </div>
      {v.safe_location === false && (
        <div role="alert" className="rounded-xl border-2 border-[#DC2626] bg-[#DC2626]/15 p-5" data-testid="unsafe-location-warning">
          <p className="flex items-center gap-2 text-lg font-extrabold uppercase text-white"><AlertTriangle className="h-6 w-6 text-[#FACC15]" aria-hidden="true" /> {t("wizard.unsafeTitle")}</p>
          <p className="mt-2 text-sm text-gray-100">{t("wizard.unsafeText")}</p>
          <a href="tel:911" className="btn-red mt-4 px-5 py-2.5 text-sm" data-testid="unsafe-call-911-btn"><Phone className="h-4 w-4" /> {t("wizard.call911")}</a>
        </div>
      )}

      <div>
        <p className="mb-2 text-sm font-semibold text-gray-200">{t("wizard.type")} <span className="text-[#DC2626]">*</span></p>
        <div role="radiogroup" className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {VEHICLE_TYPES.map((vt) => (
            <ChoiceButton key={vt} selected={v.type === vt} onClick={() => set({ type: vt })} testId={`vehicle-type-${vt}`}>{t(`vehicleTypes.${vt}`)}</ChoiceButton>
          ))}
        </div>
        {errors["vehicle.type"] && <p role="alert" className="mt-1.5 text-sm text-red-400" data-testid="vehicle-type-error">{t(errors["vehicle.type"])}</p>}
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="vehicle-make" label={t("wizard.make")} error={errors["vehicle.make"]} required>
          <input {...inputProps("vehicle-make", errors["vehicle.make"])} list="make-list" value={v.make} maxLength={60} autoCapitalize="words" onChange={(e) => set({ make: e.target.value })} />
          <datalist id="make-list">{COMMON_MAKES.map((m) => <option key={m} value={m} />)}</datalist>
        </Field>
        <Field id="vehicle-model" label={t("wizard.model")} error={errors["vehicle.model"]} required>
          <input {...inputProps("vehicle-model", errors["vehicle.model"])} value={v.model} maxLength={60} autoCapitalize="words" onChange={(e) => set({ model: e.target.value })} />
        </Field>
      </div>

      <details className="group rounded-xl border border-[#2E2E3A] bg-[#0E0E12] p-4" data-testid="vehicle-more-details">
        <summary className="cursor-pointer text-sm font-semibold text-[#FACC15]" data-testid="vehicle-more-toggle">{t("wizard.moreVehicle")}</summary>
      <div className="mt-4 space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field id="vehicle-year" label={t("wizard.year")}>
          <select {...inputProps("vehicle-year")} value={v.year} onChange={(e) => set({ year: e.target.value })}>
            <option value="">{t("wizard.selectYear")}</option>
            {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </Field>
        <Field id="vehicle-color" label={t("wizard.color")}>
          <select {...inputProps("vehicle-color")} value={v.color} onChange={(e) => set({ color: e.target.value })}>
            <option value="">{t("wizard.selectColor")}</option>
            {COLORS.map((c) => <option key={c} value={t(`colors.${c}`)}>{t(`colors.${c}`)}</option>)}
          </select>
        </Field>
      </div>

      <Field id="vehicle-special" label={t("wizard.special")}>
        <textarea {...inputProps("vehicle-special")} rows={2} maxLength={600} value={v.special_requirements} placeholder={t("wizard.specialPh")} onChange={(e) => set({ special_requirements: e.target.value })} />
      </Field>
      <Field id="vehicle-description" label={t("wizard.problem")}>
        <textarea {...inputProps("vehicle-description")} rows={3} maxLength={1500} value={v.description} placeholder={t("wizard.problemPh")} onChange={(e) => set({ description: e.target.value })} />
      </Field>
      <PhotoUpload photos={draft.photos} onChange={(photos) => update("photos", photos)} />
      </div>
      </details>
    </fieldset>
  );
};
